import React, { useEffect } from 'react';
import { Alert, Spin, Tag, Tooltip, Typography } from 'antd';

import { FX } from '../../../../state/app/constants';
import type { InspectAssessmentOptimalPortfolio } from '../../../../state/app/inspect_assessment/types';

type RiskLimitSource =
  InspectAssessmentOptimalPortfolio['instruments'][number]['risk_limit_source'];
type PmSessionSettings = NonNullable<
  InspectAssessmentOptimalPortfolio['pm_session_settings']
>;

type Props = {
  error?: string;
  fetched: boolean;
  fetching: boolean;
  hasSelectedAttempt: boolean;
  onLoad: () => void;
  portfolio?: InspectAssessmentOptimalPortfolio;
};

const SOURCE_DETAILS: Record<
  RiskLimitSource,
  { color?: string; detail: string; label: string }
> = {
  saved_session_calculation: {
    color: 'blue',
    detail:
      'Stored with the latest optimal portfolio calculated for this session. The saved map does not record the earlier source of each value.',
    label: 'Saved session calculation',
  },
  current_session_settings: {
    color: 'cyan',
    detail:
      "Rebuilt now from this session's starting capital, hard-limits setting, copied instrument percentage, and class margin.",
    label: 'Current session settings',
  },
  stored_assessment: {
    color: 'purple',
    detail: "Read from the instrument's stored assessment limit.",
    label: 'Stored assessment value',
  },
  derived_assessment: {
    color: 'gold',
    detail: "Calculated from the assessment instrument's risk type.",
    label: 'Derived assessment default',
  },
  code_fallback: {
    color: 'red',
    detail:
      'No usable stored or derived limit exists, so the calculation uses 1.',
    label: '$1 code fallback',
  },
  unavailable: {
    detail: 'No reliable limit value can be shown.',
    label: 'Unavailable',
  },
};

const formatLimit = (value?: number | null) =>
  value === null || value === undefined
    ? '—'
    : value.toLocaleString('en', {
        maximumFractionDigits: 4,
        minimumFractionDigits: 0,
      });

const formatPercent = (value: number) =>
  `${(value * 100).toLocaleString('en', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  })}%`;

const formatMultiplier = (value: number) =>
  `${value.toLocaleString('en', {
    maximumFractionDigits: 4,
    minimumFractionDigits: 0,
  })}×`;

const formatAmount = (value: number | string, currency: string) =>
  `${Number(value).toLocaleString('en', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  })} ${currency}`;

const formatOptionalAmount = (
  value: number | null,
  currency: string,
  suffix = '',
) => (value === null ? 'Not set' : `${formatAmount(value, currency)}${suffix}`);

const shortSellLabel = (settings: PmSessionSettings) => {
  if (!settings.allow_short_sell && !settings.allow_short_only_on_fx) {
    return 'Off';
  }
  const scope = settings.allow_short_sell
    ? 'all enabled instruments'
    : 'FX only';
  return `${formatPercent(settings.short_sell_limit_fraction)} · ${scope}`;
};

const PmSessionSettingsView: React.FC<{
  settings: PmSessionSettings;
}> = ({ settings }) => {
  const useAssetClassLimits =
    settings.instrument_limit_mode === 'asset_class_percent';
  const marginRows = [
    ['Bonds', settings.margins.bonds],
    ['Equity', settings.margins.equity],
    ['Equity ETFs', settings.margins.equity_etfs],
    ['Commodity ETFs', settings.margins.commodity_etfs],
    ['Futures', settings.margins.futures],
  ] as const;

  return (
    <section
      aria-label="PM session risk settings"
      className="assessment-inspect-assessment__pm-session-settings"
    >
      <header className="assessment-inspect-assessment__pm-session-settings-header">
        <div>
          <Typography.Title level={5}>PM session settings</Typography.Title>
          <Typography.Text type="secondary">
            Source: current saved values on PM session #{settings.session_id},
            selected by this assessment attempt. They are read now; they were
            not snapshotted when the assessment was scored. The assessment
            limits used are listed separately below.
          </Typography.Text>
        </div>
        <Tag color="blue">Selected PM session</Tag>
      </header>

      <dl className="assessment-inspect-assessment__pm-session-summary">
        <div>
          <dt>AUM</dt>
          <dd>{formatAmount(settings.aum, settings.reporting_currency)}</dd>
        </div>
        <div>
          <dt>Instrument-limit mode</dt>
          <dd>{settings.instrument_limit_mode_label}</dd>
        </div>
        <div>
          <dt>PM limit enforcement</dt>
          <dd>{settings.hard_limits ? 'Hard limits' : 'Soft limits'}</dd>
        </div>
        <div>
          <dt>Short-sell limit</dt>
          <dd>{shortSellLabel(settings)}</dd>
        </div>
        <div>
          <dt>FX leverage</dt>
          <dd>{formatMultiplier(settings.fx_leverage)}</dd>
        </div>
        <div>
          <dt>Net DV01 limit</dt>
          <dd>
            {formatOptionalAmount(
              settings.net_dv01_limit,
              settings.reporting_currency,
              '/bp',
            )}
          </dd>
        </div>
        <div>
          <dt>Gross DV01 limit</dt>
          <dd>
            {formatOptionalAmount(
              settings.gross_dv01_limit,
              settings.reporting_currency,
              '/bp',
            )}
          </dd>
        </div>
      </dl>

      <div className="assessment-inspect-assessment__pm-session-tables">
        <section>
          <Typography.Text strong>PM session limits</Typography.Text>
          <Typography.Text type="secondary">
            {useAssetClassLimits
              ? 'Saved fractions are shown as percentages.'
              : `Saved amounts are in ${settings.reporting_currency}.`}
          </Typography.Text>
          <div className="assessment-inspect-assessment__pm-session-table-wrap">
            <table aria-label="PM session limits">
              <thead>
                {useAssetClassLimits ? (
                  <tr>
                    <th scope="col">Asset class</th>
                    <th scope="col">Limit</th>
                  </tr>
                ) : (
                  <tr>
                    <th scope="col">Instrument</th>
                    <th scope="col">Limit</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {useAssetClassLimits
                  ? settings.asset_class_limits.map(row => (
                      <tr key={row.asset_class}>
                        <th scope="row">{row.asset_class_label}</th>
                        <td>
                          {formatPercent(row.limit_fraction)}
                          {row.asset_class === FX ? (
                            <Typography.Text
                              className="assessment-inspect-assessment__fx-limit-note"
                              type="secondary"
                            >
                              PM&apos;s combined FX limit comes from FX
                              leverage above, not this percentage.
                            </Typography.Text>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  : settings.per_asset_notional_limits.map(row => (
                      <tr key={row.underlying_asset_id}>
                        <th scope="row">{row.instrument_label}</th>
                        <td>
                          {formatAmount(
                            row.notional_limit,
                            settings.reporting_currency,
                          )}
                        </td>
                      </tr>
                    ))}
                {(useAssetClassLimits
                  ? settings.asset_class_limits
                  : settings.per_asset_notional_limits
                ).length === 0 ? (
                  <tr>
                    <td colSpan={2}>No saved limits.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <Typography.Text strong>Margins</Typography.Text>
          <Typography.Text type="secondary">
            The funded share of a position: 5% means 5% must be funded.
          </Typography.Text>
          <div className="assessment-inspect-assessment__pm-session-table-wrap">
            <table aria-label="PM session margins">
              <thead>
                <tr>
                  <th scope="col">Instrument class</th>
                  <th scope="col">Margin</th>
                </tr>
              </thead>
              <tbody>
                {marginRows.map(([label, value]) => (
                  <tr key={label}>
                    <th scope="row">{label}</th>
                    <td>{formatPercent(value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </section>
  );
};

const InstrumentRiskLimitsTable: React.FC<Props> = ({
  error,
  fetched,
  fetching,
  hasSelectedAttempt,
  onLoad,
  portfolio,
}) => {
  useEffect(() => {
    if (hasSelectedAttempt && !fetched && !fetching && !error) {
      onLoad();
    }
  }, [error, fetched, fetching, hasSelectedAttempt, onLoad]);

  if (!hasSelectedAttempt) return null;

  if (error) {
    return <Alert showIcon title={error} type="error" />;
  }

  if (!portfolio) {
    return (
      <div className="assessment-inspect-assessment__risk-limits-loading">
        {fetching ? <Spin size="small" /> : null}
        <Typography.Text type="secondary">
          {fetching
            ? 'Loading instrument risk limits…'
            : 'No limits available.'}
        </Typography.Text>
      </div>
    );
  }

  return (
    <div className="assessment-inspect-assessment__risk-limits-content">
      {portfolio.pm_session_settings ? (
        <PmSessionSettingsView settings={portfolio.pm_session_settings} />
      ) : (
        <Alert
          showIcon
          title="PM session settings are unavailable for this attempt."
          type="warning"
        />
      )}

      <header className="assessment-inspect-assessment__risk-limits-header">
        <Typography.Title level={5}>Limits used by assessment</Typography.Title>
        <Typography.Text type="secondary">
          Each row shows the number used to turn that instrument’s position into
          a share of its limit. Saved values come from the latest
          session-specific optimal-portfolio calculation; other values are
          rebuilt from current session or assessment data. Hover the source for
          details.
        </Typography.Text>
      </header>

      {portfolio.risk_limits_error ? (
        <Alert
          description={portfolio.risk_limits_error}
          showIcon
          title="Some current limits are unavailable"
          type="warning"
        />
      ) : null}

      <div className="assessment-inspect-assessment__risk-limits-table-wrap">
        <table
          aria-label="Instrument risk limits"
          className="assessment-inspect-assessment__risk-limits-table"
        >
          <thead>
            <tr>
              <th scope="col">Instrument</th>
              <th scope="col">Assessment risk limit</th>
              <th scope="col">Source</th>
            </tr>
          </thead>
          <tbody>
            {portfolio.instruments.map(instrument => {
              const source =
                SOURCE_DETAILS[instrument.risk_limit_source] ??
                SOURCE_DETAILS.unavailable;
              return (
                <tr key={instrument.instrument_id}>
                  <th scope="row">{instrument.instrument_code}</th>
                  <td>
                    {formatLimit(instrument.risk_limit)}{' '}
                    {instrument.risk_limit === null ||
                    instrument.risk_limit === undefined
                      ? ''
                      : instrument.unit}
                  </td>
                  <td>
                    <Tooltip title={source.detail}>
                      <span
                        aria-label={`${source.label}: ${source.detail}`}
                        className="assessment-inspect-assessment__risk-limit-source"
                        tabIndex={0}
                      >
                        <Tag color={source.color}>{source.label}</Tag>
                      </span>
                    </Tooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InstrumentRiskLimitsTable;
